import { func, bool } from 'prop-types'
import {
  AnimationHandler,
  Badge,
  Nameplate,
  Tile,
  TileCaption,
  TileImage,
  TileOverlay,
  HoverHandler,
} from '@mc/design-system'

import { TRENDING_TILE_TYPE } from 'types'
import Image from 'components/LegacyImage'
import Link from 'components/Link'
import { SeriesLogo } from 'components/SeriesLogo'
import { getIsSeries } from 'libs/utils'
import {
  getDuration,
  tileOptimizedImage,
  nameplateOptimizedImage,
} from 'components/GlobalCarousel'
import { useJustLaunchedTag } from 'experiments/pen-5038_v1_mega_content_picker'

import { trackTileClicked } from './events'

export const TrendingTile = ({
  tile,
  onClick = trackTileClicked,
  openNewPage = false,
  // pen-5038_v1 experiment
  isJustLaunched = false,
}) => {
  const isSeries = getIsSeries(tile.type)

  const tag = useJustLaunchedTag({
    isJustLaunched,
    originalTag: tile.releaseDescription ?? tile.tag,
  })

  return (
    <div className='col'>
      {/* if it has a backgroundImage, it's an announcement tile */}
      {tile.backgroundImage && (
        <HoverHandler>
          {({ hovering: isHovering, focused }) => (
            <Link
              href={tile.link}
              passHref
              newTab={openNewPage}
              onClick={() => onClick(tile.link)}
            >
              <Tile
                aspectRatio='9x16'
                className='mc-corners--sm mc-overflow--hidden'
              >
                <AnimationHandler type='zoom' animating={isHovering || focused}>
                  <TileImage
                    image={
                      <Image
                        alt={tile.title || ''}
                        src={tile.backgroundImage}
                        width={395}
                        height={702}
                      />
                    }
                  />
                </AnimationHandler>
              </Tile>
            </Link>
          )}
        </HoverHandler>
      )}

      {/* no backgroundImage, it's a class tile */}
      {!tile.backgroundImage && (
        <HoverHandler>
          {({ hovering: isHovering, focused }) => (
            <Link
              href={tile.link}
              className='d-block mc-corners--sm mc-overflow--hidden'
              passHref
              newTab={openNewPage}
              onClick={() => onClick(tile.link)}
            >
              <Tile aspectRatio='9x16'>
                {tag && (
                  <TileCaption position='left top' padding={4}>
                    <Badge>
                      <span className='mc-text--capitalize'>{tag}</span>
                    </Badge>
                  </TileCaption>
                )}

                <TileOverlay />

                <AnimationHandler type='zoom' animating={isHovering || focused}>
                  <TileImage
                    image={
                      // explicitly use a standard `img` element so we can deterministically preload images
                      // eslint-disable-next-line @next/next/no-img-element
                      <img alt='' src={tileOptimizedImage(tile.primary2x3)} />
                    }
                  />
                </AnimationHandler>

                <TileCaption padding={4} position='center bottom' flush>
                  {isSeries && <SeriesLogo width={137} />}
                  <Nameplate
                    size='small'
                    image={
                      // explicitly use a standard `img` element so we can deterministically preload images
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        alt={isSeries ? tile.title : tile.instructorName}
                        src={nameplateOptimizedImage(tile.nameplateImage)}
                        style={{ width: '100%' }}
                      />
                    }
                    separator={!isSeries}
                    subText={!isSeries ? tile.headline : undefined}
                  />
                  <span className='mc-mt-3 mc-text-x-small'>
                    {getDuration(tile.link, tile.duration)}
                  </span>
                </TileCaption>
              </Tile>
            </Link>
          )}
        </HoverHandler>
      )}
    </div>
  )
}

TrendingTile.propTypes = {
  tile: TRENDING_TILE_TYPE.isRequired,
  onClick: func,
  openNewPage: bool,
  // pen-5038_v1 experiment
  isJustLaunched: bool,
}
